/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  React  */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const { t } = i18n;

export const usePutUserSelf = () => {
    const [saving, setSaving] = useState(false);

    const putUserSelf = useCallback(async ({ params }) => {
        setSaving(true);

        let response;
        try {
            response = await axios.put('/user/self', params);
        } catch (err) {
            setSaving(false);
            const errMsg = err?.data?.message || t('Common.error');

            throw new Error(errMsg);
        }

        setSaving(false);

        return response;
    }, []);

    return { putUserSelf, saving };
};

export const usePutUser = () => {
    const [saving, setSaving] = useState(false);

    const putUser = useCallback(async ({ id, params }) => {
        try {
            setSaving(true);
            await axios.put(`/user/${id}`, params);
            setSaving(false);
            return true; // success = true
        } catch (err) {
            debug.e(t('Users.put.notSuccess'), err);
            setSaving(false);
            return false; // success = false
        }
    }, []);

    return { putUser, saving };
};

// Hook to fetch the /user data async
export const useFetchUsersAsync = () => {
    const error = useRef(null);
    const foundData = useRef(false);
    const userData = useRef([]);

    const getUsers = useCallback(async (p) => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const { params } = p;
        const { platformTenantId } = params;
        params.size = -1;

        if (platformTenantId) {
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/user', {
                cancelToken: source.token,
                params
            });
            if (Response.hasData(response)) {
                userData.current = Response.getData(response);
                foundData.current = true;
            } else {
                userData.current = [];
                foundData.current = false;
            }
        } catch (err) {
            error.current = err;
            debug.e('Error Getting User Information', err);
        }
        return {
            error: error.current,
            foundData: foundData.current,
            userData: userData.current
        };
    }, []);
    return { getUsers };
};

// Hook to fetch the /user response
const defaultObject = {};
// Always define the params object outside the component using it, otherwise
// you will keep rendering and re-rendering the component.
export const useFetchUsers = ({ clientId = '', params = defaultObject }) => {
    const [users, setUsers] = useState([]);
    const [userCount, setUserCount] = useState(0);
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    const parameters = useMemo(
        () =>
            clientId
                ? {
                      ...params,
                      includeClients: true,
                      platformTenantId: clientId
                  }
                : params,
        [clientId, params]
    );

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/user', {
                    cancelToken: source.token,
                    params: parameters
                });
                if (!Response.hasData(response)) {
                    debug.e('No user found.');
                } else {
                    const usersResponse = Response.getData(response);
                    setUsers(usersResponse);
                    setUserCount(usersResponse?.length);
                }
            } catch (error) {
                debug.e('Unable to get users.', error);
            }
            setLoading(false);
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [clientId, parameters, shouldRefetchData]);

    return {
        loading,
        refetchData,
        userCount,
        users
    };
};

// Hook to fetch the /user/:id response
/**
 * Hook to fetch a single user by ID from /user/:id endpoint
 * @param {Object} params - Hook parameters
 * @param {string|null} [params.clientId] - Optional client ID to filter by platform tenant
 * @param {string} params.userId - ID of user to fetch
 * @returns {{loading, refetchData, user}} Object containing:
 *   - loading {boolean} - Loading state
 *   - refetchData {function} - Function to trigger data refetch
 *   - user {User|null} - User data if found
 */
export const useFetchSingleUser = ({ clientId = null, userId }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    const parameters = useMemo(
        () =>
            clientId
                ? {
                      includeClients: true,
                      platformTenantId: clientId
                  }
                : {},
        [clientId]
    );

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`/user/${userId}`, {
                    cancelToken: source.token,
                    params: parameters
                });
                if (!Response.hasData(response)) {
                    debug.e('No user found.');
                } else {
                    const userResponse = Response.getOne(response);
                    setUser(userResponse);
                }
            } catch (error) {
                debug.e('Unable to get user.', error);
            }
            setLoading(false);
        };

        if (userId) {
            fetchData();
        }

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [clientId, parameters, shouldRefetchData, userId]);

    return {
        loading,
        refetchData,
        user
    };
};
